import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Plus } from 'lucide-react';
import { PrepAdminLayout } from '../../../components/prep/PrepAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blank = { title: '', description: '', type: 'medical_assisting', price: 0, duration: '', features: '' };

export const PrepAdminPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [isCreating, setIsCreating] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => {
    axios.get(`${API}/prep/admin/programs`, { withCredentials: true })
      .then(r => setPrograms(r.data))
      .catch(() => axios.get(`${API}/prep/programs`).then(r => setPrograms(r.data)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(blank); setIsCreating(true); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, features: p.features.join('\n') }); setIsCreating(false); setOpen(true); };

  const save = async () => {
    const data = { ...form, features: form.features.split('\n').filter(f => f.trim()) };
    try {
      if (isCreating) {
        await axios.post(`${API}/prep/programs`, data, { withCredentials: true });
        toast.success('Program created');
      } else {
        await axios.put(`${API}/prep/programs/${form.id}`, data, { withCredentials: true });
        toast.success('Program updated');
      }
      setOpen(false);
      load();
    } catch {
      toast.error('Failed to save program');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this program?')) return;
    try {
      await axios.delete(`${API}/prep/programs/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const toggle = async (id) => {
    try {
      await axios.patch(`${API}/prep/programs/${id}/toggle`, {}, { withCredentials: true });
      load();
    } catch { toast.error('Toggle failed'); }
  };

  return (
    <PrepAdminLayout title="Manage Programs">
      <div className="mb-6">
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-program-btn">
          <Plus size={16} className="mr-2" /> Add Program
        </Button>
      </div>

      {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : (
        <div className="space-y-4">
          {programs.map((p) => (
            <Card key={p.id} className="border-2">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <CardTitle>{p.title}</CardTitle>
                      <Badge className={p.is_active ? 'bg-green-600' : 'bg-gray-500'}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">{p.description}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-program-${p.id}`}>
                      <Edit size={15} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(p.id)} className="text-red-600" data-testid={`delete-program-${p.id}`}>
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 mb-3 text-sm">
                  <div><span className="text-gray-500">Type</span><p className="font-medium capitalize">{p.type.replace('_', ' ')}</p></div>
                  <div><span className="text-gray-500">Duration</span><p className="font-medium">{p.duration}</p></div>
                  <div><span className="text-gray-500">Price</span><p className="font-medium text-blue-600">${p.price}</p></div>
                  <div><span className="text-gray-500">Enrolled</span><p className="font-medium">{p.enrollment_count}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <Label>Active</Label>
                  <Switch checked={p.is_active} onCheckedChange={() => toggle(p.id)} />
                </div>
              </CardContent>
            </Card>
          ))}
          {programs.length === 0 && <p className="text-center text-gray-500 py-8">No programs yet.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isCreating ? 'Create Program' : 'Edit Program'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm">
                  <option value="medical_assisting">Medical Assisting</option>
                  <option value="phlebotomy">Phlebotomy</option>
                  <option value="allied_health">Allied Health</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div><Label>Duration</Label><Input value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} placeholder="8 weeks" /></div>
            </div>
            <div><Label>Price ($)</Label><Input type="number" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Features (one per line)</Label><Textarea rows={5} value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} placeholder="40+ hours of video lessons&#10;Practice tests" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700">{isCreating ? 'Create' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PrepAdminLayout>
  );
};
